import { ALLERGEN_ISSUE_CODES, type AllergenIssueCode, type IssueSeverity } from './issues';
import type { AllergenSurface, AllergenSurfacePolicy } from './types';

/**
 * Tabella unica delle politiche per superficie. Preserva il comportamento di
 * OGGI: ogni scostamento fra superfici e' una riga di questa tabella.
 * Note di prodotto (DEV-375): il cartellino congela la contaminazione nel
 * payload (adapter, non politica); la voce `INGREDIENT_ADMIN` fornisce solo le
 * dichiarazioni e i conteggi del badge, mentre lo stato a tre valori
 * (`allergenState`) e' calcolato dal SQL `allergenStateByRoot`, non dal nucleo.
 */

function severityTable(
  entries: Partial<Record<AllergenIssueCode, IssueSeverity>>,
): Record<AllergenIssueCode, IssueSeverity> {
  const table = {} as Record<AllergenIssueCode, IssueSeverity>;
  for (const code of ALLERGEN_ISSUE_CODES) {
    table[code] = entries[code] ?? 'IGNORE';
  }
  return table;
}

const NO_NESTED_SILENCING = {
  rootBreakdownExemptionSilencesNested: false,
  carryOverLinkedChildSilencesNested: false,
} as const;

/**
 * Cartellino da banco (`LabelConfirmationService` + `CounterRegisterService`).
 * - NEVER_DECLARED / UNDECLARED_COMPOSITION / UNLINKED_ROW solo avviso: DEV-366
 *   (`counter-label.md`), il cartello stampa "Allergeni: da verificare".
 * - MISSING_BREAKDOWN blocca solo per `isCompound === true`.
 * - Sostanza assente su un CONTAINS: art. 21 §1 lett. b Reg. UE 1169/2011.
 * - MAY_CONTAIN senza nome: il registro lancia `corrupt()` -> mai stampabile.
 * - L'esenzione breakdown della radice silenzia MISSING_BREAKDOWN e
 *   MISSING_ADDITIVE_NAME annidati, e un figlio collegato carry-over (art. 20
 *   lett. b Reg. UE 1169/2011) non richiede scomposizione: il testo non li
 *   stampa (DEV-375 S7#1/#2). Gli allergeni restano sempre dichiarati e un
 *   discendente mai dichiarato continua ad avvisare.
 * - Contaminazione tenant congelata nel payload firmato (D4): l'adapter passa
 *   l'elenco letto dalla conferma, mai live.
 */
const COUNTER_LABEL_POLICY: AllergenSurfacePolicy = {
  surface: 'COUNTER_LABEL',
  mergeKey: 'ALLERGEN_SUBSTANCE',
  mayContainGranularity: 'SUBSTANCE_WHEN_KNOWN',
  dedupeMayContainAgainstContains: true,
  includeTenantContamination: true,
  applyAnnexIIExemptions: true,
  usesMeasuredSo2: false,
  rootBreakdownExemptionSilencesNested: true,
  carryOverLinkedChildSilencesNested: true,
  severity: severityTable({
    UNLINKED_ROW: 'WARN',
    NEVER_DECLARED: 'WARN',
    UNDECLARED_COMPOSITION: 'WARN',
    MISSING_BREAKDOWN: 'BLOCK',
    KEYLESS_CONTAINS: 'BLOCK',
    MAY_CONTAIN_NAME_MISSING: 'BLOCK',
    MISSING_ADDITIVE_NAME: 'BLOCK',
    COMPOSITION_LIMIT: 'BLOCK',
    COMPOSITION_OUT_OF_SCOPE: 'BLOCK',
    RECIPE_GRAPH_LIMIT: 'BLOCK',
    ANNEX_II_EXEMPTION_APPLIED: 'INFO',
    ANNEX_II_EXEMPTION_NOT_APPLIED: 'INFO',
    SULPHITE_CONTENT_UNKNOWN: 'INFO',
  }),
};

/**
 * Preimballato (`PrepackagedLabelComposerService`, snapshot congelato).
 * - NEVER_DECLARED / UNDECLARED_COMPOSITION / MISSING_BREAKDOWN: stesso
 *   difetto («nessuna scomposizione»), BLOCK (LEGAL, D3): l'etichetta
 *   preimballata non stampa mai un elenco incompleto.
 * - «Puo' contenere» per sostanza quando nota, per categoria solo quando la
 *   sostanza non e' nota: stessa regola del cartellino da banco (D2).
 * - Un «puo' contenere» coperto da un CONTAINS alla granularita' stampata non
 *   si ripete (D1/S7#1): niente «Contiene: mandorle» + «Puo' contenere: Frutta
 *   a guscio» sullo stesso foglio.
 * - Contaminazione tenant solo opt-in congelata (LEGAL): l'adapter passa
 *   l'elenco soltanto quando il lotto l'ha scelta.
 * - SO2 misurato sul finito solo qui (LEGAL, Allegato II punto 12).
 * - L'esenzione breakdown della radice e il figlio carry-over silenziano i
 *   rilievi annidati: il testo non li stampa.
 */
const PREPACKAGED_POLICY: AllergenSurfacePolicy = {
  surface: 'PREPACKAGED',
  mergeKey: 'ALLERGEN_SUBSTANCE',
  mayContainGranularity: 'SUBSTANCE_WHEN_KNOWN',
  dedupeMayContainAgainstContains: true,
  includeTenantContamination: true,
  applyAnnexIIExemptions: true,
  usesMeasuredSo2: true,
  rootBreakdownExemptionSilencesNested: true,
  carryOverLinkedChildSilencesNested: true,
  severity: severityTable({
    NEVER_DECLARED: 'BLOCK',
    UNDECLARED_COMPOSITION: 'BLOCK',
    MISSING_BREAKDOWN: 'BLOCK',
    KEYLESS_CONTAINS: 'BLOCK',
    MAY_CONTAIN_NAME_MISSING: 'BLOCK',
    MISSING_ADDITIVE_NAME: 'BLOCK',
    COMPOSITION_LIMIT: 'BLOCK',
    COMPOSITION_OUT_OF_SCOPE: 'BLOCK',
    LEGACY_FROZEN_FIELD: 'WARN',
    ANNEX_II_EXEMPTION_APPLIED: 'INFO',
    ANNEX_II_EXEMPTION_NOT_APPLIED: 'INFO',
    SULPHITE_CONTENT_UNKNOWN: 'INFO',
  }),
};

/**
 * Modalita' libera del preimballato (spec §6.2): gli errori di dominio dati
 * scendono a warning («non verificato»). Sostituisce il set `nonDowngradable`
 * del composer: un link fuori scope non ha nemmeno dichiarazioni note, quindi
 * non si declassa mai.
 */
const PREPACKAGED_FREE_POLICY: AllergenSurfacePolicy = {
  ...PREPACKAGED_POLICY,
  surface: 'PREPACKAGED_FREE',
  severity: severityTable({
    NEVER_DECLARED: 'WARN',
    UNDECLARED_COMPOSITION: 'WARN',
    MISSING_BREAKDOWN: 'WARN',
    KEYLESS_CONTAINS: 'WARN',
    MAY_CONTAIN_NAME_MISSING: 'WARN',
    MISSING_ADDITIVE_NAME: 'WARN',
    COMPOSITION_LIMIT: 'WARN',
    COMPOSITION_OUT_OF_SCOPE: 'BLOCK',
    LEGACY_FROZEN_FIELD: 'WARN',
    ANNEX_II_EXEMPTION_APPLIED: 'INFO',
    ANNEX_II_EXEMPTION_NOT_APPLIED: 'INFO',
    SULPHITE_CONTENT_UNKNOWN: 'INFO',
  }),
};

/**
 * Letture derivate da `AllergenAggregationService.aggregateByRecipe` /
 * `aggregateIngredientSubjects`: fusione per (allergene, sostanza), CONTAINS
 * prima delle esenzioni dell'Allegato II (solo CONTAINS con sostanza di
 * catalogo), contaminazione tenant sempre.
 */
const AGGREGATION_BASE = {
  mergeKey: 'ALLERGEN_SUBSTANCE',
  mayContainGranularity: 'SUBSTANCE_WHEN_KNOWN',
  dedupeMayContainAgainstContains: true,
  includeTenantContamination: true,
  applyAnnexIIExemptions: true,
  usesMeasuredSo2: false,
  ...NO_NESTED_SILENCING,
} as const;

/** Vista ricetta (backoffice): solo diagnostica, nessun gate. */
const RECIPE_VIEW_POLICY: AllergenSurfacePolicy = {
  ...AGGREGATION_BASE,
  surface: 'RECIPE_VIEW',
  severity: severityTable({
    UNLINKED_ROW: 'WARN',
    NEVER_DECLARED: 'WARN',
    UNDECLARED_COMPOSITION: 'INFO',
    MISSING_BREAKDOWN: 'INFO',
    KEYLESS_CONTAINS: 'INFO',
    COMPOSITION_LIMIT: 'WARN',
    COMPOSITION_OUT_OF_SCOPE: 'WARN',
    RECIPE_GRAPH_LIMIT: 'WARN',
  }),
};

/**
 * Menu pubblico (QR): un elenco allergeni non verificabile non si pubblica.
 * Ingrediente-soggetto mai dichiarato e limiti di composizione NASCONDONO la
 * voce (`withheldIngredientIds` di `aggregateIngredientSubjects`): "allergeni:
 * nessuno" sarebbe un falso sul canale che il cliente allergico legge. Una
 * riga di ricetta mai dichiarata resta un avviso (la ricetta si pubblica).
 */
const MENU_PUBLIC_POLICY: AllergenSurfacePolicy = {
  ...AGGREGATION_BASE,
  surface: 'MENU_PUBLIC',
  severity: severityTable({
    UNLINKED_ROW: 'WARN',
    NEVER_DECLARED: 'WARN',
    KEYLESS_CONTAINS: 'INFO',
    COMPOSITION_LIMIT: 'BLOCK',
    COMPOSITION_OUT_OF_SCOPE: 'BLOCK',
    RECIPE_GRAPH_LIMIT: 'BLOCK',
  }),
  rowSeverityOverrides: {
    INGREDIENT_SUBJECT: { NEVER_DECLARED: 'BLOCK' },
  },
};

/** Completezza del menu per il backoffice: mostra i difetti, non nasconde la voce. */
const MENU_ADMIN_POLICY: AllergenSurfacePolicy = {
  ...RECIPE_VIEW_POLICY,
  surface: 'MENU_ADMIN',
};

/**
 * Manuale HACCP (`ManualDataService`): come il menu admin, ma il «puo'
 * contenere» si stampa per categoria (LEGAL, divergenza documentata in
 * `recipes.md`).
 */
const HACCP_MANUAL_POLICY: AllergenSurfacePolicy = {
  ...RECIPE_VIEW_POLICY,
  surface: 'HACCP_MANUAL',
  mayContainGranularity: 'CATEGORY',
};

/**
 * `AllergenAggregationService.aggregate()` alimenta `LotAllergen` (PK
 * `[lotId, allergenId]`): una riga per allergene, ma con le stesse
 * diagnostiche (e la stessa contaminazione tenant) della vista ricetta.
 */
const LOT_AGGREGATE_POLICY: AllergenSurfacePolicy = {
  ...RECIPE_VIEW_POLICY,
  surface: 'LOT_AGGREGATE',
  mergeKey: 'ALLERGEN',
};

/**
 * Lotto (`LotAllergen`, PK `[lotId, allergenId]`): una riga per allergene.
 * Ignora ogni stato "non verificato" (LEGAL, commentato in
 * `deriveStandingAllergensByIngredient`: il lotto congela gli allergeni noti).
 * Nessuna contaminazione tenant: `deriveStandingAllergensByIngredient` non la
 * aggiunge (la aggiunge solo `aggregate()` per la vista ricetta).
 */
const LOT_POLICY: AllergenSurfacePolicy = {
  ...AGGREGATION_BASE,
  surface: 'LOT',
  mergeKey: 'ALLERGEN',
  includeTenantContamination: false,
  severity: severityTable({}),
};

/**
 * Stato allergeni dell'anagrafica ingredienti (filtri/indicatori, readiness):
 * nessuna esenzione dell'Allegato II e nessuna contaminazione del tenant
 * (LEGAL: il filtro cerca cio' che il prodotto "contiene o puo' contenere",
 * non cio' che si stampa). Il badge conta le dichiarazioni per (allergene,
 * sostanza) e il «puo' contenere» non si deduplica contro il «contiene»:
 * sono le due serie distinte che il conteggio mostra.
 */
const INGREDIENT_ADMIN_POLICY: AllergenSurfacePolicy = {
  ...AGGREGATION_BASE,
  surface: 'INGREDIENT_ADMIN',
  dedupeMayContainAgainstContains: false,
  includeTenantContamination: false,
  applyAnnexIIExemptions: false,
  severity: severityTable({
    NEVER_DECLARED: 'WARN',
    UNDECLARED_COMPOSITION: 'WARN',
    MISSING_BREAKDOWN: 'WARN',
    KEYLESS_CONTAINS: 'WARN',
    MISSING_ADDITIVE_NAME: 'WARN',
    COMPOSITION_LIMIT: 'WARN',
    COMPOSITION_OUT_OF_SCOPE: 'WARN',
  }),
};

export const ALLERGEN_SURFACE_POLICIES: Readonly<Record<AllergenSurface, AllergenSurfacePolicy>> = {
  RECIPE_VIEW: RECIPE_VIEW_POLICY,
  MENU_PUBLIC: MENU_PUBLIC_POLICY,
  MENU_ADMIN: MENU_ADMIN_POLICY,
  HACCP_MANUAL: HACCP_MANUAL_POLICY,
  LOT: LOT_POLICY,
  LOT_AGGREGATE: LOT_AGGREGATE_POLICY,
  COUNTER_LABEL: COUNTER_LABEL_POLICY,
  PREPACKAGED: PREPACKAGED_POLICY,
  PREPACKAGED_FREE: PREPACKAGED_FREE_POLICY,
  INGREDIENT_ADMIN: INGREDIENT_ADMIN_POLICY,
};
